import React from 'react';
import { AbstractButton, Flex, Icon, IconButton, Tooltip } from 'pounceV2';
import { useTheme } from '@mui/material';
import { useHistory } from 'react-router-dom';
import useCopyToClipboardV3 from 'Hooks/useCopyToClipboardV3';
import urls from 'Source/urls';
import {
  addFavoritePrompt,
  removeFavoritePrompt,
  isPromptFavorited,
} from './utils/favoritePrompts';

interface PromptActionsProps {
  prompt: string;
}

const PromptActionsV3: React.FC<PromptActionsProps> = ({ prompt }) => {
  const {
    palette: { tokens, componentTokens },
  } = useTheme();
  const history = useHistory();
  const copyToClipboard = useCopyToClipboardV3();
  const [isFavorited, setIsFavorited] = React.useState(() => isPromptFavorited(prompt));

  React.useEffect(() => {
    setIsFavorited(isPromptFavorited(prompt));
  }, [prompt]);

  const handleToggleFavorite = React.useCallback(() => {
    if (isFavorited) {
      removeFavoritePrompt(prompt);
      setIsFavorited(false);
    } else {
      addFavoritePrompt(prompt);
      setIsFavorited(true);
    }
  }, [prompt, isFavorited]);

  return (
    <Flex align="center" gap={1}>
      <Tooltip content="Copy prompt" placement="top">
        <IconButton
          icon="copy"
          size="small"
          variant="ghost"
          aria-label="Copy prompt"
          onClick={() => copyToClipboard(prompt)}
        />
      </Tooltip>
      <Tooltip content={isFavorited ? 'Remove from favorites' : 'Add to favorites'} placement="top">
        <AbstractButton
          width={32}
          height={32}
          borderRadius="xlarge"
          backgroundColor="transparent"
          display="flex"
          alignItems="center"
          justifyContent="center"
          transition="background-color 150ms ease"
          aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          onClick={handleToggleFavorite}
          _hover={{
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.hover,
            '& .MuiSvgIcon-root': {
              color: isFavorited
                ? tokens.content.assets.accent[5].base
                : componentTokens.buttons.colors.coreIcon.ghost.assets.hover,
            },
          }}
        >
          <Icon
            type={isFavorited ? 'star' : 'star-outline'}
            size="medium"
            color={
              isFavorited
                ? tokens.content.assets.accent[5].base
                : componentTokens.buttons.colors.coreIcon.ghost.text.resting
            }
          />
        </AbstractButton>
      </Tooltip>
      <Tooltip content="Schedule prompt" placement="top">
        <IconButton
          icon="clock"
          size="small"
          variant="ghost"
          aria-label="Schedule prompt"
          onClick={() =>
            history.push(urls.ai.scheduledPrompts.list(), { schedulePromptText: prompt })
          }
        />
      </Tooltip>
    </Flex>
  );
};

export default React.memo(PromptActionsV3);
